import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { doc, setDoc, getDoc, collection, getDocs, query, where, deleteField, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { exchangeConnectorService } from '../../services/exchangeConnector';

interface TDAmeritradeConfigProps {
  onConfigSaved?: () => void;
}

export const TDAmeritradeConfig: React.FC<TDAmeritradeConfigProps> = ({ onConfigSaved }) => {
  const { state } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [accountId, setAccountId] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [migrated, setMigrated] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!state.user) return;

      try {
        // First check for credentials in the new api_credentials subcollection
        const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
        const tdQuery = query(apiCredentialsRef, where("provider", "==", "tdameritrade"));
        const querySnapshot = await getDocs(tdQuery);
        
        if (!querySnapshot.empty) {
          const credentialDoc = querySnapshot.docs[0];
          const credentialData = credentialDoc.data();
          
          if (credentialData) {
            setApiKey(credentialData.apiKey || '');
            setAccountId(credentialData.accountId || '');
            setCallbackUrl(credentialData.callbackUrl || '');
            console.log('Loaded TD Ameritrade API keys from api_credentials subcollection');
            return;
          }
        }
        
        // Fallback: Check for credentials in the legacy locations
        let foundInLegacyLocation = false;
        
        // 1. Check tdameritrade_config collection
        const docRef = doc(db, 'tdameritrade_config', state.user.id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.apiKey) {
            setApiKey(data.apiKey || '');
            setAccountId(data.accountId || '');
            setCallbackUrl(data.callbackUrl || '');
            foundInLegacyLocation = true;
            console.log('Found TD Ameritrade API keys in tdameritrade_config collection (legacy location)');
          }
        }
        
        // 2. Check user document preferences
        if (!foundInLegacyLocation) {
          const userRef = doc(db, 'users', state.user.id);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const tdConfig = userData.preferences?.apiKeys?.tdameritrade;
            
            if (tdConfig && tdConfig.apiKey) {
              setApiKey(tdConfig.apiKey || '');
              setAccountId(tdConfig.accountId || '');
              setCallbackUrl(tdConfig.callbackUrl || '');
              foundInLegacyLocation = true;
              console.log('Found TD Ameritrade API keys in user preferences (legacy location)');
            }
          }
        }
        
        // If found in legacy location, migrate to new location
        if (foundInLegacyLocation && apiKey) {
          setMigrated(true);
          
          // Automatically migrate to new location
          await setDoc(doc(apiCredentialsRef), {
            provider: "tdameritrade",
            apiKey,
            accountId,
            callbackUrl,
            userId: state.user.id,
            email: state.user.email,
            updatedAt: new Date().toISOString(),
            migratedFrom: "legacy_storage"
          });
          
          console.log('Migrated TD Ameritrade API keys from legacy location to api_credentials subcollection');
          
          // Remove from user preferences if it exists there
          const userRef = doc(db, 'users', state.user.id);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.preferences?.apiKeys?.tdameritrade) {
              try {
                await updateDoc(userRef, {
                  "preferences.apiKeys.tdameritrade": deleteField()
                });
                console.log('Removed TD Ameritrade API keys from user preferences after migration');
              } catch (err) {
                console.error('Error removing TD Ameritrade API keys from user preferences:', err);
              }
            }
          }
          
          // Remove from tdameritrade_config collection
          try {
            await setDoc(docRef, { 
              apiKey: deleteField(),
              accountId: deleteField(),
              callbackUrl: deleteField(),
              migratedTo: "api_credentials",
              migratedAt: new Date().toISOString()
            }, { merge: true });
            console.log('Removed TD Ameritrade API keys from tdameritrade_config collection after migration');
          } catch (err) {
            console.error('Error removing TD Ameritrade API keys from tdameritrade_config collection:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching TD Ameritrade config:', error);
      }
    };

    fetchConfig();
  }, [state.user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    if (!state.user) {
      setMessage({ type: 'error', text: 'You must be logged in to save configuration.' });
      setLoading(false);
      return;
    }

    if (!apiKey) {
      setMessage({ type: 'error', text: 'API Key is required.' });
      setLoading(false);
      return;
    }

    try {
      // Save to the new api_credentials subcollection
      const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
      const tdQuery = query(apiCredentialsRef, where("provider", "==", "tdameritrade"));
      const querySnapshot = await getDocs(tdQuery);
      
      if (!querySnapshot.empty) {
        const credentialDoc = querySnapshot.docs[0];
        await updateDoc(credentialDoc.ref, {
          apiKey,
          accountId,
          callbackUrl,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await setDoc(doc(apiCredentialsRef), {
          provider: "tdameritrade",
          apiKey,
          accountId,
          callbackUrl,
          userId: state.user.id,
          email: state.user.email,
          updatedAt: new Date().toISOString(),
        });
      }
      
      // If migrated, ensure API keys are removed from preferences
      if (migrated) {
        const userRef = doc(db, 'users', state.user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.preferences?.apiKeys?.tdameritrade) {
            try {
              await updateDoc(userRef, {
                "preferences.apiKeys.tdameritrade": deleteField()
              });
              console.log('Removed TD Ameritrade API keys from user preferences after save');
            } catch (err) {
              console.error('Error removing TD Ameritrade API keys from user preferences:', err);
            }
          }
        }
      }

      // Automatically establish connection using the exchange connector service
      console.log('Initializing TD Ameritrade connection after configuration...');
      const connectionResult = await exchangeConnectorService.initializeSingleConnection('tdameritrade', state.user.id);
      
      if (connectionResult) {
        console.log('TD Ameritrade connection established automatically after configuration');
      } else {
        console.warn('Failed to establish automatic TD Ameritrade connection after configuration');
      }

      setMessage({ type: 'success', text: 'TD Ameritrade configuration saved successfully!' });
      onConfigSaved?.();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving configuration. Please try again.' });
      console.error('Error saving TD Ameritrade config:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          TD Ameritrade Configuration
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Account ID"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            margin="normal"
            required
            helperText="Enter your TD Ameritrade account ID"
          />
          
          <TextField
            fullWidth
            label="Callback URL"
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            margin="normal"
            placeholder="https://your-app.com/callback"
            helperText="Optional: Only needed for OAuth authentication"
          />

          {message && (
            <Alert severity={message.type} sx={{ mt: 2, mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Configuration'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};
